import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Kolom } from '@xxtract/tabelfilters';
import { Button, Card, CardContent, CardHeader, CardTitle, StatusBadge, Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@xxtract/ui';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { FilterRejectedMessage, isFilterRejected } from '@/components/feedback/FilterRejectedMessage';
import { ariaSort, Filterbalk, SorteerbareKop, useServerTabelData, useTabelStand, type ServerAntwoord } from '@/components/tabel';
import { useProcessItems, useRefreshProcessItems } from '../hooks/usePhysicalItems';
import { formatFetchedAt, stepLabel } from '../format';
import { PROCESS_FILTER_TEKSTEN } from './ProcessFilters';
import { DIENST_ITEMS_LIJST, FYSIEKE_ITEMS_LIJST, PROCES_ITEMS_PAGINAGROOTTE, PROCESSEN_PAGINAFILTER, procesFilterVelden, procesItemsSleutel, ladderPlek, stapkeuzesOpProcesvolgorde } from '../procesLijsten';
import type { CanonicalProcessName, FunnelStep, PhysicalItem, PhysicalItemsFilters } from '../processes.api';

/** Opmaakklassen, geen zichtbare tekst. */
const CHEVRON_OPEN = 'rotate-90';
/** Voorvoegsel van de testids van de geweigerd-filtermelding (`proces-producten-filter-rejected`). */
const FILTER_TEST_ID = 'proces-producten';

/** De lijstkolommen die de tabel als kop toont, in beeldvolgorde; `sinds` filtert alleen (datumfilter). */
const KOPPEN = ['product', 'klant', 'stap', 'werkdagen', 'toestand'] as const;

/**
 * Brok 3 van `mockup-processen-detail.html`: de producten in dit proces. Eén regel per product met
 * de stap waarin het staat, hoe lang al, en of het stilstaat.
 *
 * Geen productnaam maar het GTIN: de bron kent namen alleen via de orderboom, en daar hoort 3% van
 * de producten bij (meting 2026-08-17). Een half gevulde naamkolom is misleidender dan een nummer.
 */
/**
 * De verwachte volgende stap: de eerstvolgende sport van de ladder die de bron voor dit proces
 * levert (`steps`, op volgorde). Staat het product op de laatste sport, of kent de ladder de huidige
 * stap niet, dan is er niets te verwachten en tonen we ook niets — nooit een verzonnen vervolg.
 */
/** De stapcode voor werk zonder gemelde stap; de BFF zet hem als laatste stap in de ladder. */
const UNKNOWN_STEP = 'UNKNOWN';

function verwachteVolgendeStap(ladder: FunnelStep[], canonicalStep: string): string | null {
  const index = ladder.findIndex((step) => step.canonicalStep === canonicalStep);
  if (index < 0 || index >= ladder.length - 1) return null;
  const volgende = ladder[index + 1]!.canonicalStep;
  // "Stap onbekend" staat altijd achteraan de ladder (spec-processen-actuele-stand CAP-11), maar is
  // geen stap waar een product naartoe gaat.
  return volgende === UNKNOWN_STEP ? null : volgende;
}

/**
 * `filters` (story 3, klantfilter): alleen het fysieke proces krijgt ze mee van het detailscherm;
 * de acht diensten houden hun lijst zoals hij is.
 */
/**
 * `filterNotApplied` (story 4): the detail page says which active filter does not reach this list —
 * `'all'` for a service list (nothing reaches it), `'periodStatus'` for the physical list.
 */
export function ProductenPerProces({ name, ladder, filters, filterNotApplied }: { name: CanonicalProcessName; ladder: FunnelStep[]; filters?: PhysicalItemsFilters; filterNotApplied?: 'all' | 'periodStatus' }) {
  const { t, i18n } = useTranslation();
  // spec-tabelfilters story 8: kolomfilters (ook de stap), sortering en pagina in de lijststand per
  // proces; een ander proces heeft een eigen stand. Filteren en sorteren gebeurt op de server, zodat
  // het totaal bij het filter hoort.
  const lijst = name === 'physical' ? FYSIEKE_ITEMS_LIJST : DIENST_ITEMS_LIJST;
  const sleutel = procesItemsSleutel(name);
  const stand = useTabelStand(lijst, sleutel, { afhankelijkVan: PROCESSEN_PAGINAFILTER, paginagrootte: PROCES_ITEMS_PAGINAGROOTTE });
  const velden = useMemo(() => procesFilterVelden(lijst), [lijst]);
  const titelRef = useRef<HTMLHeadingElement>(null);
  const context = useMemo(() => ({ t: (s: string) => t(s) }), [t]);
  const standaard = lijst.standaardSortering?.(context);
  // Met de standsleutel erbij: een ander proces hergebruikt de component, en dan hoort het oude totaal er niet bij.
  const [antwoord, setAntwoord] = useState<{ sleutel: string; a: ServerAntwoord }>();
  const tabel = useServerTabelData(lijst, stand, antwoord?.sleutel === sleutel ? antwoord.a : undefined);
  const { page: _page, pageSize: _pageSize, ...kolommen } = tabel.parameters;
  const itemsQuery = useProcessItems(name, tabel.pagina, undefined, filters, kolommen);
  const refreshItems = useRefreshProcessItems(name, tabel.pagina);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openItem, setOpenItem] = useState<string | null>(null);

  // Een bewaard kolomfilter dat de server weigert (400) sluit het tabblad niet af: de melding biedt de weg terug.
  const filterRejected = isFilterRejected(itemsQuery.error, velden, itemsQuery.data?.data.sources.procesManagement);
  const data = filterRejected ? undefined : itemsQuery.data?.data;
  const physicalUnavailable = name === 'physical' && data?.sources.procesManagementEvents?.status === 'unavailable';
  const meta = data?.meta;
  useEffect(() => {
    if (meta) setAntwoord({ sleutel, a: { totaal: meta.total, facetten: meta.facetten, suggesties: meta.suggesties, afgekapt: meta.afgekapt } });
  }, [meta, sleutel]);
  const kolomfilterActief = Object.keys(stand.filters).length > 0;

  if (itemsQuery.isError && !filterRejected) {
    return <ApiErrorMessage error={itemsQuery.error} onRetry={() => itemsQuery.refetch()} />;
  }

  async function handleRefresh() {
    setIsRefreshing(true);
    try {
      await refreshItems();
    } finally {
      setIsRefreshing(false);
    }
  }

  // Dezelfde drie standen en dezelfde badges als de fysieke productenlijst — één beeldtaal.
  // De middelste heet anders per proces: bij fysiek betekent een niet-gestarte klok "nog niet binnen",
  // bij een dienst "nog niet klaar om op te pakken". Hetzelfde feit, twee verschillende woorden.
  function statusFor(item: PhysicalItem) {
    if (item.stagnating) return { status: 'process-on-hold' as const, label: t('processes.physicalItems.status.stagnating') };
    if (item.notReceived) {
      const sleutel = name === 'physical' ? 'processes.physicalItems.status.notReceived' : 'processes.physicalItems.status.notPickable';
      return { status: 'process-future' as const, label: t(sleutel) };
    }
    return { status: 'process-current' as const, label: t('processes.physicalItems.status.onTrack') };
  }

  return (
    <Card data-testid="proces-producten">
      <CardHeader className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle ref={titelRef} tabIndex={-1}>{t('processes.detail.productsTitle')}</CardTitle>
          {data ? <p className="text-xs text-[var(--foreground-muted)]">{t('processes.updatedAt', { time: formatFetchedAt(data.fetchedAt, i18n.language) })}</p> : null}
        </div>
        {data ? (
          <Button data-testid="proces-producten-refresh" disabled={isRefreshing} onClick={handleRefresh} size="sm" variant="secondary">
            <span aria-hidden="true" className="material-symbols-outlined text-[16px]">refresh</span>
            {t('processes.refresh')}
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {filterNotApplied != null ? (
          <p className="text-[11px] text-[var(--info-text)]" data-testid="proces-producten-filter-not-applied">
            {t(filterNotApplied === 'all' ? 'processes.allCustomers.list' : 'processes.allCustomers.physicalCounts')}
          </p>
        ) : null}
        {/* Stapkeuzes in ladder- (proces)volgorde, niet op label (besluit Friso 27-09-2026). */}
        <Filterbalk context={context} data={{ ...tabel, keuzes: stapkeuzesOpProcesvolgorde(tabel.keuzes, stand.filters, ladderPlek(ladder)) }} lijst={lijst} stand={stand} />

        <FilterRejectedMessage
          baseTestId={FILTER_TEST_ID}
          error={itemsQuery.error}
          focusNaWissen={titelRef}
          onClear={stand.reset}
          rejected={filterRejected}
          teksten={PROCESS_FILTER_TEKSTEN}
          velden={velden}
        />

        {itemsQuery.isLoading ? <div className="h-40 animate-pulse rounded-[var(--radius-control)] bg-[var(--background-muted)]" data-testid="proces-producten-loading" /> : null}

        {/* Fysiek leunt op de set lopende orders: afgekapt betekent daar dat niet alle orders zijn opgehaald (CAP-8). */}
        {data?.meta.truncated ? (
          <p className="text-[11px] text-[var(--info-text)]" data-testid="proces-producten-truncated" role="status">
            {name === 'physical' ? t('processes.ordersTruncated.lijst') : t('processes.physicalItems.truncated', { count: data.meta.sourceEventTotal })}
          </p>
        ) : null}

        {/* Fysiek en de bron onbereikbaar: geen "geen producten" — dat zou lezen als "niets in huis". */}
        {physicalUnavailable ? (
          <p className="text-[11px] text-[var(--info-text)]" data-testid="proces-producten-degraded" role="status">{t('processes.degraded.title')}</p>
        ) : null}

        {/* Voorrang: onbereikbare bron eerst; "geen treffers" alleen door een actief kolomfilter. */}
        {data && data.items.length === 0 && !physicalUnavailable ? (
          <p className="text-xs text-[var(--foreground-muted)]" data-testid="proces-producten-leeg">
            {t(kolomfilterActief && data.meta.total === 0 ? 'tabel.geenTreffers' : name === 'physical' ? 'processes.physicalItems.empty' : 'processes.detail.productsEmpty')}
          </p>
        ) : null}

        {data && data.items.length > 0 ? (
          <>
            {/* Op een smal scherm scrolt de tabel binnen de kaart, niet de hele pagina (visuele controle 24-09-2026, 375 px). */}
            <div className="overflow-x-auto">
            <Table>
              {/* Het bijschrift noemt de standaardvolgorde; bij een eigen sortering zeggen de koppen het. */}
              {stand.sortering.length === 0 ? <TableCaption>{t('processes.physicalItems.caption')}</TableCaption> : null}
              <TableHeader>
                <TableRow>
                  {/* Met de klantkolom: zonder lijken twee regels met dezelfde streepjescode een dubbeling; het
                      zijn verschillende producten (andere klant of doelmarkt) in verschillende stappen. */}
                  {KOPPEN.map((sleutel) => (
                    <TableHead aria-sort={ariaSort(stand.sortering, sleutel)} key={sleutel} scope="col">
                      <SorteerbareKop
                        kolom={lijst.kolommen.find((k) => k.sleutel === sleutel)! as Kolom<never, never>}
                        onKlik={() => stand.klikOpKop(sleutel)}
                        sortering={stand.sortering}
                        standaard={standaard}
                      />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((item) => {
                  const state = statusFor(item);
                  const isOpen = openItem === item.tradeItemId;
                  const volgende = verwachteVolgendeStap(ladder, item.canonicalStep);
                  return (
                    <Fragment key={item.tradeItemId}>
                      <TableRow data-testid={`proces-product-${item.tradeItemId}`}>
                        <TableCell>
                          <button
                            aria-expanded={isOpen}
                            className="flex items-center gap-1.5 text-left hover:underline"
                            data-testid={`proces-product-toggle-${item.tradeItemId}`}
                            onClick={() => setOpenItem((current) => (current === item.tradeItemId ? null : item.tradeItemId))}
                            type="button"
                          >
                            <span aria-hidden="true" className={`material-symbols-outlined text-[16px] transition-transform ${isOpen ? CHEVRON_OPEN : ''}`}>chevron_right</span>
                            {item.gtin ?? item.tradeItemId}
                          </button>
                        </TableCell>
                        <TableCell>{item.gln ?? t('common.notAvailable')}</TableCell>
                        <TableCell>{stepLabel(item.canonicalStep, t)}</TableCell>
                        <TableCell>{item.workdaysInStep != null ? t('processes.physicalItems.workdays', { count: item.workdaysInStep }) : t('common.notAvailable')}</TableCell>
                        <TableCell>
                          <StatusBadge announce={false} label={state.label} status={state.status} />
                        </TableCell>
                      </TableRow>
                      {isOpen ? (
                        <TableRow data-testid={`proces-product-tijdlijn-${item.tradeItemId}`}>
                          <TableCell className="bg-[var(--background-muted)]" colSpan={5}>
                            <ol className="space-y-1">
                              {item.timelineTruncated ? (
                                <li className="text-[11px] text-[var(--foreground-muted)]">{t('processes.detail.timelineTruncated')}</li>
                              ) : null}
                              {item.timeline.map((entry) => (
                                <li className="flex flex-wrap items-baseline gap-2 text-xs" key={`${entry.canonicalStep}-${entry.at}`}>
                                  <span className="font-medium text-[var(--foreground)]">{stepLabel(entry.canonicalStep, t)}</span>
                                  <span className="text-[var(--foreground-muted)]">{formatFetchedAt(entry.at, i18n.language)}</span>
                                </li>
                              ))}
                              {volgende != null ? (
                                <li className="flex flex-wrap items-baseline gap-2 text-xs" data-testid={`proces-product-verwacht-${item.tradeItemId}`}>
                                  <span className="text-[var(--foreground-muted)]">{stepLabel(volgende, t)}</span>
                                  <span className="text-[var(--foreground-muted)]">{t('processes.detail.timelineExpected')}</span>
                                </li>
                              ) : null}
                            </ol>
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </div>

            <div className="flex items-center justify-between gap-3">
              <Button data-testid="proces-producten-vorige" disabled={tabel.pagina <= 1} onClick={() => stand.zetPagina(tabel.pagina - 1)} size="sm" variant="secondary">
                {t('common.previous')}
              </Button>
              <p className="text-xs text-[var(--foreground-muted)]">
                {t('processes.physicalItems.pageOf', { page: tabel.pagina, total: Math.max(1, Math.ceil(data.meta.total / data.meta.pageSize)) })}
                <span className="ml-2" data-testid="proces-producten-treffers">{t('tabel.treffers', { count: data.meta.total })}</span>
              </p>
              <Button data-testid="proces-producten-volgende" disabled={!data.meta.hasMore} onClick={() => stand.zetPagina(tabel.pagina + 1)} size="sm" variant="secondary">
                {t('common.next')}
              </Button>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
